import { InvalidTransitionError, ValidationError } from "./errors";
import { assertCents, assertCurrency, multiply, type Cents } from "./money";
import { computeTax, TAX_MODES, type TaxMode } from "./tax";

export type QuoteStatus = "draft" | "sent" | "viewed" | "accepted" | "cancelled";
export type Language = "es" | "en";
export type PaymentMethod = "shopify" | "transfer";

export const LANGUAGES: readonly Language[] = ["es", "en"];
export const PAYMENT_METHODS: readonly PaymentMethod[] = ["shopify", "transfer"];

export interface Client {
  name: string;
  /** ISO 3166-1 alpha-2, e.g. MX, ES, GT. */
  country: string;
  taxId?: string;
}

export interface Contact {
  name: string;
  email: string;
  role?: string;
}

export interface QuoteItem {
  id: string;
  concept: string;
  description: string;
  quantity: number;
  unitPriceCents: Cents;
  /** Optional items are shown to the client but only count when selected. */
  optional: boolean;
  selected: boolean;
}

export interface Acceptance {
  name: string;
  position: string;
  email: string;
  ip?: string;
}

/** Serializable state of a quote. This is what repositories persist. */
export interface QuoteData {
  id: string;
  folio: string;
  status: QuoteStatus;
  language: Language;
  currency: string;
  client: Client;
  contacts: Contact[];
  title: string;
  intro: string;
  terms: string;
  items: QuoteItem[];
  taxMode: TaxMode;
  paymentMethod: PaymentMethod;
  /** ISO date (YYYY-MM-DD). */
  validUntil: string;
  createdAt: string;
  updatedAt: string;
  sentAt?: string;
  viewedAt?: string;
  acceptedAt?: string;
  acceptedBy?: Acceptance;
  cancelledAt?: string;
  shopifyDraftOrderId?: string;
  /** Where the client pays (Shopify invoice link), once the order exists. */
  paymentUrl?: string;
}

/** Fields the admin can edit while the quote is a draft. */
export type QuoteEditableFields = Pick<
  QuoteData,
  | "language"
  | "currency"
  | "client"
  | "contacts"
  | "title"
  | "intro"
  | "terms"
  | "items"
  | "taxMode"
  | "paymentMethod"
  | "validUntil"
>;

export interface QuoteTotals {
  subtotalCents: Cents;
  taxCents: Cents;
  totalCents: Cents;
  taxMode: TaxMode;
  currency: string;
}

/**
 * Pure totals calculation, shared with the frontend for live previews.
 * Optional items count only when selected.
 */
export function computeTotals(
  items: ReadonlyArray<Pick<QuoteItem, "quantity" | "unitPriceCents" | "optional" | "selected">>,
  taxMode: TaxMode,
  currency: string,
): QuoteTotals {
  const subtotalCents = items
    .filter((item) => !item.optional || item.selected)
    .reduce((sum, item) => sum + multiply(item.unitPriceCents, item.quantity), 0);
  const taxCents = computeTax(subtotalCents, taxMode);
  return { subtotalCents, taxCents, totalCents: subtotalCents + taxCents, taxMode, currency };
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COUNTRY = /^[A-Z]{2}$/;

/**
 * Aggregate root for a quote. Holds the invariants and the state machine:
 * draft → sent → viewed → accepted; cancelled from any non-accepted state.
 * "expired" is derived from validUntil, never stored.
 */
export class Quote {
  private constructor(private data: QuoteData) {}

  static create(input: {
    id: string;
    folio: string;
    now: Date;
    fields: QuoteEditableFields;
  }): Quote {
    const fields = validateEditableFields(input.fields);
    const timestamp = input.now.toISOString();
    return new Quote({
      id: input.id,
      folio: input.folio,
      status: "draft",
      createdAt: timestamp,
      updatedAt: timestamp,
      ...fields,
    });
  }

  static fromData(data: QuoteData): Quote {
    return new Quote({ ...data });
  }

  toData(): QuoteData {
    return structuredClone(this.data);
  }

  get id(): string {
    return this.data.id;
  }

  get folio(): string {
    return this.data.folio;
  }

  get status(): QuoteStatus {
    return this.data.status;
  }

  get contacts(): readonly Contact[] {
    return this.data.contacts;
  }

  /** True when the quote is past its validity date and was never accepted. */
  isExpired(now: Date): boolean {
    if (this.data.status === "accepted" || this.data.status === "cancelled") return false;
    const endOfDay = new Date(`${this.data.validUntil}T23:59:59.999Z`);
    return now.getTime() > endOfDay.getTime();
  }

  totals(): QuoteTotals {
    return computeTotals(this.data.items, this.data.taxMode, this.data.currency);
  }

  /** Replaces the editable fields. Only allowed while the quote is a draft. */
  update(fields: QuoteEditableFields, now: Date): void {
    this.requireStatus("draft", "edit");
    Object.assign(this.data, validateEditableFields(fields), { updatedAt: now.toISOString() });
  }

  /** Builds a new draft with the same content, a new identity and no history. */
  duplicate(input: { id: string; folio: string; now: Date; validUntil: string }): Quote {
    const { language, currency, client, contacts, title, intro, terms, items, taxMode, paymentMethod } =
      this.data;
    return Quote.create({
      id: input.id,
      folio: input.folio,
      now: input.now,
      fields: {
        language,
        currency,
        client: { ...client },
        contacts: contacts.map((c) => ({ ...c })),
        title,
        intro,
        terms,
        items: items.map((item) => ({ ...item })),
        taxMode,
        paymentMethod,
        validUntil: input.validUntil,
      },
    });
  }

  markSent(now: Date): void {
    this.requireStatus("draft", "send");
    if (this.data.contacts.length === 0) {
      throw new ValidationError("a quote needs at least one contact to be sent");
    }
    if (this.isExpired(now)) {
      throw new InvalidTransitionError("cannot send a quote whose validity date has passed");
    }
    this.transition("sent", now, { sentAt: now.toISOString() });
  }

  /** True for a contact listed on the quote (emails are stored lower-case). */
  hasContact(email: string): boolean {
    const needle = email.trim().toLowerCase();
    return this.data.contacts.some((c) => c.email === needle);
  }

  /** Clients only see quotes that were sent to them; drafts and cancelled quotes stay private. */
  isVisibleToClients(): boolean {
    return this.data.status === "sent" || this.data.status === "viewed" || this.data.status === "accepted";
  }

  get language(): Language {
    return this.data.language;
  }

  get validUntil(): string {
    return this.data.validUntil;
  }

  /** Records the first time a client opened the quote. Idempotent after the first view. */
  markViewed(now: Date): void {
    if (this.data.status !== "sent") return;
    this.transition("viewed", now, { viewedAt: now.toISOString() });
  }

  /**
   * Accepts the quote. When `selectedOptionalItemIds` is given, the client's choice
   * of optional items replaces the defaults before the totals are frozen.
   */
  accept(by: Acceptance, now: Date, selectedOptionalItemIds?: readonly string[]): void {
    if (this.data.status !== "sent" && this.data.status !== "viewed") {
      throw new InvalidTransitionError(`cannot accept a quote in status ${this.data.status}`);
    }
    if (this.isExpired(now)) {
      throw new InvalidTransitionError("cannot accept an expired quote");
    }
    if (!by.name?.trim() || !by.position?.trim()) {
      throw new ValidationError("acceptance requires name and position");
    }
    if (selectedOptionalItemIds) this.selectOptionalItems(selectedOptionalItemIds);
    const acceptedBy: Acceptance = { name: by.name.trim(), position: by.position.trim(), email: by.email };
    if (by.ip) acceptedBy.ip = by.ip;
    this.transition("accepted", now, { acceptedAt: now.toISOString(), acceptedBy });
  }

  private selectOptionalItems(ids: readonly string[]): void {
    const optionalIds = new Set(this.data.items.filter((i) => i.optional).map((i) => i.id));
    for (const id of ids) {
      if (!optionalIds.has(id)) throw new ValidationError(`item ${id} is not an optional item of this quote`);
    }
    const chosen = new Set(ids);
    this.data.items = this.data.items.map((item) => (item.optional ? { ...item, selected: chosen.has(item.id) } : item));
  }

  get paymentMethod(): PaymentMethod {
    return this.data.paymentMethod;
  }

  cancel(now: Date): void {
    if (this.data.status === "accepted") {
      throw new InvalidTransitionError("an accepted quote cannot be cancelled");
    }
    if (this.data.status === "cancelled") return;
    this.transition("cancelled", now, { cancelledAt: now.toISOString() });
  }

  attachDraftOrder(draftOrderId: string, paymentUrl: string, now: Date): void {
    this.requireStatus("accepted", "attach a draft order to");
    this.data.shopifyDraftOrderId = draftOrderId;
    this.data.paymentUrl = paymentUrl;
    this.data.updatedAt = now.toISOString();
  }

  private requireStatus(status: QuoteStatus, action: string): void {
    if (this.data.status !== status) {
      throw new InvalidTransitionError(
        `cannot ${action} a quote in status ${this.data.status} (requires ${status})`,
      );
    }
  }

  private transition(status: QuoteStatus, now: Date, patch: Partial<QuoteData>): void {
    Object.assign(this.data, patch, { status, updatedAt: now.toISOString() });
  }
}

function validateEditableFields(fields: QuoteEditableFields): QuoteEditableFields {
  if (!LANGUAGES.includes(fields.language)) {
    throw new ValidationError("language must be es or en");
  }
  assertCurrency(fields.currency);
  if (!TAX_MODES.includes(fields.taxMode)) {
    throw new ValidationError("taxMode must be iva16 or exempt");
  }
  if (!PAYMENT_METHODS.includes(fields.paymentMethod)) {
    throw new ValidationError("paymentMethod must be shopify or transfer");
  }
  if (!fields.title?.trim()) {
    throw new ValidationError("title is required");
  }
  if (!ISO_DATE.test(fields.validUntil) || Number.isNaN(Date.parse(fields.validUntil))) {
    throw new ValidationError("validUntil must be a date in YYYY-MM-DD format");
  }
  const client = validateClient(fields.client);
  const contacts = fields.contacts.map(validateContact);
  const seen = new Set<string>();
  for (const contact of contacts) {
    if (seen.has(contact.email)) throw new ValidationError(`duplicate contact ${contact.email}`);
    seen.add(contact.email);
  }
  const items = fields.items.map(validateItem);
  if (items.length === 0) {
    throw new ValidationError("a quote needs at least one item");
  }
  return {
    language: fields.language,
    currency: fields.currency,
    client,
    contacts,
    title: fields.title.trim(),
    intro: fields.intro ?? "",
    terms: fields.terms ?? "",
    items,
    taxMode: fields.taxMode,
    paymentMethod: fields.paymentMethod,
    validUntil: fields.validUntil,
  };
}

function validateClient(client: Client): Client {
  if (!client?.name?.trim()) throw new ValidationError("client.name is required");
  const country = client.country?.toUpperCase();
  if (!country || !COUNTRY.test(country)) {
    throw new ValidationError("client.country must be an ISO 3166-1 alpha-2 code");
  }
  const result: Client = { name: client.name.trim(), country };
  if (client.taxId?.trim()) result.taxId = client.taxId.trim();
  return result;
}

function validateContact(contact: Contact): Contact {
  if (!contact?.name?.trim()) throw new ValidationError("contact.name is required");
  const email = contact.email?.trim().toLowerCase();
  if (!email || !EMAIL.test(email)) throw new ValidationError(`invalid contact email: ${contact.email}`);
  const result: Contact = { name: contact.name.trim(), email };
  if (contact.role?.trim()) result.role = contact.role.trim();
  return result;
}

function validateItem(item: QuoteItem): QuoteItem {
  if (!item?.id?.trim()) throw new ValidationError("item.id is required");
  if (!item.concept?.trim()) throw new ValidationError("item.concept is required");
  if (typeof item.quantity !== "number" || !(item.quantity > 0)) {
    throw new ValidationError(`item ${item.concept}: quantity must be greater than zero`);
  }
  assertCents(item.unitPriceCents, `item ${item.concept}: unitPriceCents`);
  const optional = Boolean(item.optional);
  return {
    id: item.id.trim(),
    concept: item.concept.trim(),
    description: item.description ?? "",
    quantity: item.quantity,
    unitPriceCents: item.unitPriceCents,
    optional,
    selected: optional ? Boolean(item.selected) : true,
  };
}
